import React, { useState, useEffect } from "react";
import "./Chatbot.css";

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [currentStep, setCurrentStep] = useState("initial");
  const [markets, setMarkets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedMarkets, setHasFetchedMarkets] = useState(false);

  // API base URL
  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

  // Initial options for the user
  const initialOptions = [
    { id: 1, text: "Market Rates", value: "market", icon: "fas fa-store" },
    {
      id: 2,
      text: "Weather Information",
      value: "weather",
      icon: "fas fa-cloud-sun",
    },
  ];

  // Bankura blocks for weather information
  const bankuraBlocks = [
    "Bankura Town",
    "Bishnupur",
    "Khatra",
    "Taldangra",
    "Saltora",
    "Chhatna",
    "Gangajalghati",
    "Mejhia",
    "Indpur",
    "Sonamukhi",
    "Patrasayer",
    "Indus",
    "Barjora",
    "Onda",
    "Kotulpur",
    "Jaypur",
    "Sarenga",
    "Raipur",
    "Ranibandh",
    "Hirbandh",
    "Simlapal",
  ];

  // Fetch markets from API
  const fetchMarkets = async () => {
    if (hasFetchedMarkets && markets.length > 0) {
      return markets;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/api/markets`);

      if (!response.ok) {
        throw new Error("Failed to fetch markets");
      }

      const data = await response.json();

      if (data && Array.isArray(data) && data.length > 0) {
        setMarkets(data);
        setHasFetchedMarkets(true);
        setIsLoading(false);
        return data;
      } else {
        throw new Error("Empty market data received");
      }
    } catch (error) {
      console.error("Error fetching markets:", error);
      setIsLoading(false);
      const sampleMarkets = getSampleMarkets();
      setMarkets(sampleMarkets);
      setHasFetchedMarkets(true);
      return sampleMarkets;
    }
  };

  // Sample data fallbacks
  const getSampleMarkets = () => [
    {
      id: 1,
      name: "Bankura Market",
      crops: [
        { id: 1, name: "Rice", price: 22, unit: "kg" },
        { id: 2, name: "Wheat", price: 20, unit: "kg" },
        { id: 3, name: "Potato", price: 12, unit: "kg" },
      ],
    },
    {
      id: 2,
      name: "Bishnupur Market",
      crops: [
        { id: 1, name: "Rice", price: 21, unit: "kg" },
        { id: 2, name: "Wheat", price: 19, unit: "kg" },
        { id: 3, name: "Potato", price: 13, unit: "kg" },
      ],
    },
    {
      id: 3,
      name: "Khatra Market",
      crops: [
        { id: 1, name: "Rice", price: 23, unit: "kg" },
        { id: 2, name: "Mustard", price: 45, unit: "kg" },
        { id: 3, name: "Potato", price: 14, unit: "kg" },
      ],
    },
  ];

  // Fetch real-time weather data from API
  const fetchRealTimeWeather = async (locationName) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${API_BASE}/api/weather/${encodeURIComponent(locationName)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch weather data");
      }

      const data = await response.json();
      setIsLoading(false);
      return data;
    } catch (error) {
      console.error("Error fetching weather data:", error);
      setIsLoading(false);
      return getSampleWeatherData(locationName);
    }
  };

  const getSampleWeatherData = (locationName) => ({
    blockName: locationName,
    temperature: Math.floor(Math.random() * 10) + 25,
    condition: ["Sunny", "Partly Cloudy", "Cloudy", "Light Rain"][
      Math.floor(Math.random() * 4)
    ],
    humidity: Math.floor(Math.random() * 30) + 50,
    windSpeed: Math.floor(Math.random() * 10) + 5,
    isSampleData: true,
  });

  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { text, sender: "user" }]);
  };

  const addBotMessage = (text, withOptions = false, options = []) => {
    setMessages((prev) => [
      ...prev,
      { text, sender: "bot", withOptions, options },
    ]);
  };

  const handleOptionSelect = async (option) => {
    addUserMessage(option.text);

    if (currentStep === "initial") {
      if (option.value === "market") {
        const marketData = await fetchMarkets();
        if (marketData && marketData.length > 0) {
          const marketOptions = marketData.map((market) => ({
            id: market.id,
            text: market.name,
            value: market.name,
            icon: "fas fa-store",
          }));
          addBotMessage("Please select a market:", true, marketOptions);
          setCurrentStep("select_market");
        } else {
          addBotMessage(
            "I'm having trouble loading market data. Please try again later."
          );
        }
      } else if (option.value === "weather") {
        const locationOptions = bankuraBlocks.map((block) => ({
          id: block,
          text: block,
          value: block,
          icon: "fas fa-location-dot",
        }));
        addBotMessage("Please select a Bankura block:", true, locationOptions);
        setCurrentStep("select_weather_location");
      }
    } else if (currentStep === "select_market") {
      const market = markets.find((m) =>
        m.name.toLowerCase().includes(option.text.toLowerCase())
      );

      if (market) {
        let message = `📊 **Market rates for ${market.name}:**\n\n`;
        if (market.crops && market.crops.length > 0) {
          market.crops.forEach((crop) => {
            message += `🌾 ${crop.name}: ₹${crop.price}/${crop.unit}\n`;
          });
        } else {
          message += "No crop data available at the moment.";
        }
        addBotMessage(message);
        setCurrentStep("initial");
        addBotMessage(
          "Is there anything else I can help you with?",
          true,
          initialOptions
        );
      }
    } else if (currentStep === "select_weather_location") {
      const location = bankuraBlocks.find((block) =>
        block.toLowerCase().includes(option.text.toLowerCase())
      );

      if (location) {
        const weatherData = await fetchRealTimeWeather(location);

        if (weatherData) {
          const weatherIcon =
            weatherData.condition === "Sunny"
              ? "☀️"
              : weatherData.condition === "Cloudy"
              ? "☁️"
              : weatherData.condition === "Rain"
              ? "🌧️"
              : "🌤️";

          const message =
            `${weatherIcon} **Weather in ${weatherData.blockName}**\n\n` +
            `🌡️ Temperature: ${weatherData.temperature}°C\n` +
            `🌈 Condition: ${weatherData.condition}\n` +
            `💧 Humidity: ${weatherData.humidity}%\n` +
            `💨 Wind Speed: ${weatherData.windSpeed} km/h`;

          addBotMessage(message);
          setCurrentStep("initial");
          addBotMessage(
            "Is there anything else I can help you with?",
            true,
            initialOptions
          );
        }
      }
    }
  };

  const handleSendMessage = async () => {
    if (inputText.trim() === "") return;

    const userMessage = inputText;
    addUserMessage(userMessage);
    setInputText("");

    if (currentStep === "initial") {
      if (userMessage.toLowerCase().includes("market")) {
        const marketData = await fetchMarkets();
        if (marketData && marketData.length > 0) {
          const marketOptions = marketData.map((market) => ({
            id: market.id,
            text: market.name,
            value: market.name,
            icon: "fas fa-store",
          }));
          addBotMessage("Please select a market:", true, marketOptions);
          setCurrentStep("select_market");
        }
      } else if (userMessage.toLowerCase().includes("weather")) {
        const locationOptions = bankuraBlocks.map((block) => ({
          id: block,
          text: block,
          value: block,
          icon: "fas fa-location-dot",
        }));
        addBotMessage("Please select a Bankura block:", true, locationOptions);
        setCurrentStep("select_weather_location");
      } else {
        addBotMessage(
          "Hi! I can help you with market rates or weather information for Bankura district. What would you like to know?",
          true,
          initialOptions
        );
      }
    } else if (currentStep === "select_market") {
      const market = markets.find((m) =>
        m.name.toLowerCase().includes(userMessage.toLowerCase())
      );

      if (market) {
        let message = `📊 **Market rates for ${market.name}:**\n\n`;
        if (market.crops && market.crops.length > 0) {
          market.crops.forEach((crop) => {
            message += `🌾 ${crop.name}: ₹${crop.price}/${crop.unit}\n`;
          });
        }
        addBotMessage(message);
        setCurrentStep("initial");
        addBotMessage(
          "Is there anything else I can help you with?",
          true,
          initialOptions
        );
      }
    } else if (currentStep === "select_weather_location") {
      const location = bankuraBlocks.find((block) =>
        block.toLowerCase().includes(userMessage.toLowerCase())
      );

      if (location) {
        const weatherData = await fetchRealTimeWeather(location);

        if (weatherData) {
          const weatherIcon =
            weatherData.condition === "Sunny"
              ? "☀️"
              : weatherData.condition === "Cloudy"
              ? "☁️"
              : weatherData.condition === "Rain"
              ? "🌧️"
              : "🌤️";

          const message =
            `${weatherIcon} **Weather in ${weatherData.blockName}**\n\n` +
            `🌡️ Temperature: ${weatherData.temperature}°C\n` +
            `🌈 Condition: ${weatherData.condition}\n` +
            `💧 Humidity: ${weatherData.humidity}%\n` +
            `💨 Wind Speed: ${weatherData.windSpeed} km/h`;

          addBotMessage(message);
          setCurrentStep("initial");
          addBotMessage(
            "Is there anything else I can help you with?",
            true,
            initialOptions
          );
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const openChatbot = () => {
    setIsMinimized(false);
    setIsOpen(true);
    if (messages.length === 0) {
      addBotMessage(
        "👋 Hello! I'm your Shamayita Math Assistant. I can help you with:",
        true,
        initialOptions
      );
    }
  };

  const minimizeChatbot = () => {
    setIsMinimized(true);
    setIsOpen(false);
    setIsMaximized(false);
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const closeChatbot = () => {
    setIsOpen(false);
    setIsMinimized(true);
    setIsMaximized(false);
  };

  useEffect(() => {
    const messagesContainer = document.querySelector(".chatbot-messages");
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);

  return (
    <>
      {isMaximized && (
        <div className="chatbot-overlay active" onClick={minimizeChatbot} />
      )}

      <div className="chatbot-container">
        {isMinimized && (
          <div className="chatbot-minimized" onClick={openChatbot}>
            <div className="chatbot-icon">
              <i className="fas fa-comment-dots"></i>
            </div>
            <span>Chat with us</span>
          </div>
        )}

        {isOpen && (
          <div className={`chatbot-window ${isMaximized ? "maximized" : ""}`}>
            <div className="chatbot-header">
              <div className="header-content">
                <div className="bot-avatar">
                  <i className="fas fa-robot"></i>
                </div>
                <div className="header-text">
                  <h4>Shamayita Math Assistant</h4>
                  <span className="status">Online</span>
                </div>
              </div>
              <div className="header-actions">
                <button
                  className="chatbot-btn minimize"
                  onClick={minimizeChatbot}
                >
                  <i className="fas fa-minus"></i>
                </button>
                <button
                  className="chatbot-btn maximize"
                  onClick={toggleMaximize}
                >
                  <i
                    className={`fas ${
                      isMaximized ? "fa-compress" : "fa-expand"
                    }`}
                  ></i>
                </button>
                <button className="chatbot-btn close" onClick={closeChatbot}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg, index) => (
                <div key={index} className="message-container">
                  <div className={`message ${msg.sender}-message`}>
                    <div className="message-content">
                      {msg.text.split("\n").map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  </div>
                  {msg.withOptions && (
                    <div className="chatbot-options">
                      {msg.options.map((option) => (
                        <button
                          key={option.id}
                          className="option-btn"
                          onClick={() => handleOptionSelect(option)}
                        >
                          <i className={option.icon}></i>
                          {option.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="message-container">
                  <div className="message bot-message">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="chatbot-input">
              <div className="input-container">
                <input
                  type="text"
                  placeholder="Type your message here..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <button
                  className="send-btn"
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputText.trim()}
                >
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Chatbot;
